import { useEffect } from 'react'
import { Device } from '@capacitor/device'
import { useNavigate } from 'react-router'
import { util } from '~/utils/util'
import { callApi } from './axiosHelper'
import { PushNotifications } from '@capacitor/push-notifications'
import { registerPlugin } from '@capacitor/core'
import { initializeApp, getApps } from 'firebase/app'
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging'
import { constants } from '~/utils/constants'

// Native iOS plugin (ios/App/App/NovuFcmPlugin.swift) that turns the raw APNs
// token @capacitor/push-notifications gets on ios into an FCM token via the
// Firebase SDK, so Novu can deliver ios push through its fcm integration
// instead of a native apns one. android already gets an FCM token directly
// from @capacitor/push-notifications, so it doesn't need this plugin.
interface NovuFcmPlugin {
    getToken(): Promise<{ token: string }>
    addListener(
        eventName: 'tokenReceived',
        listenerFunc: (event: { token: string }) => void
    ): Promise<{ remove: () => Promise<void> }>
}

const NovuFcm = registerPlugin<NovuFcmPlugin>('NovuFcm')

type NotificationClickData = {
    targetScreen?: string
    blocker?: boolean
    clickToCall?: boolean
    mobile?: string
    isChat?: boolean
    params?: { url?: string }
}

const firebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
}

const useNovuPush = (employee_id: string) => {
    const navigate = useNavigate()

    useEffect(() => {
          console.log("'Novu push employee_id",employee_id)

        if (!employee_id || ['null', 'undefined'].includes(employee_id)) return

        const setupPush = async () => {
            try {
                const { platform } = await Device.getInfo()

                 console.log("'Novu push platform",platform)

                if (platform === 'android') {
                    await initMobileNovuPush()
                } else if (platform === 'ios') {
                console.log("'Novu push initIosNovuPush",platform)
                    await initIosNovuPush()
                } else if (platform === 'web') {
                    await initWebNovuPush()
                }
            } catch (error) {
                console.error('Novu push setup error:', error)
            }
        }

        setupPush()

        const handleServiceWorkerMessage = (event: MessageEvent) => {
            if (event.data?.type === 'novu-notification-click') {
                handleNotificationClick(event.data.payload)
            }
        }

        if (typeof navigator !== 'undefined' && navigator.serviceWorker) {
            navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage)
        }

        return () => {
            if (typeof navigator !== 'undefined' && navigator.serviceWorker) {
                navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage)
            }
        }
    }, [employee_id])

    // ---- Android (Capacitor -> FCM -> Novu) ----
    const initMobileNovuPush = async () => {
        try {
            const { receive } = await PushNotifications.requestPermissions()

            if (receive !== 'granted') {
                console.log('Novu push permission not granted:', receive)
                return
            }

            await PushNotifications.register()

            PushNotifications.addListener('registration', (token) => {
                registerNovuDeviceToken(token.value, 'android')
            })

            PushNotifications.addListener('registrationError', (error) => {
                console.error('Novu push registration error:', error)
            })

            // Fires when the user taps a notification (app in background/killed)
            PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
                const data = action?.notification?.data
                if (data) handleNotificationClick(data)
            })
        } catch (error) {
            console.error('Novu mobile push setup error:', error)
        }
    }

   const initIosNovuPush = async () => {
    try {
        const { receive } = await PushNotifications.requestPermissions()

        console.log('Novu push receive:', receive)

        if (receive !== 'granted') {
            console.log('Novu push permission not granted:', receive)
            return
        }

        // Register listeners FIRST
        await PushNotifications.addListener('registration', async (token) => {
            console.log('Novu push registration token:', token.value, token)

            // ios only gets the raw APNs token here; swap it for the FCM token
            // NovuFcmPlugin derives once AppDelegate hands Firebase that APNs token.
            try {
                const { token: fcmToken } = await NovuFcm.getToken()
                console.log('Novu push ios fcm token:', fcmToken)
                registerNovuDeviceToken(fcmToken, 'ios')
            } catch (error) {
                console.error('Failed to fetch ios FCM token:', error)
            }
        })

        await PushNotifications.addListener('registrationError', (error) => {
            console.error('Novu push registration error:', error)
        })

        await PushNotifications.addListener(
            'pushNotificationActionPerformed',
            (action) => {
                console.log('Push notification action:', action)

                const data = action?.notification?.data

                if (data) {
                    handleNotificationClick(data)
                }
            }
        )

        // Fires on token rotation while the app is running, so the backend
        // keeps a live token instead of one Firebase has since invalidated.
        NovuFcm.addListener('tokenReceived', (event) => {
            console.log('Novu push ios fcm tokenReceived:', event.token)
            registerNovuDeviceToken(event.token, 'ios')
        })

        // Register AFTER listeners are attached
        await PushNotifications.register()

        console.log('Novu push registration called')
    } catch (error) {
        console.error('Novu ios push setup error:', error)
    }
}

    // ---- Web (Firebase Cloud Messaging -> Novu) ----
    const initWebNovuPush = async () => {
        try {
            const supported = await isSupported()
            if (!supported) {
                console.log('Web push is not supported in this browser')
                return
            }

            if (Notification.permission === 'denied') {
                console.log('Notification permission was previously denied')
                return
            }

            const permission = await Notification.requestPermission()
            if (permission !== 'granted') {
                console.log('Web push permission not granted:', permission)
                return
            }

            const swParams = new URLSearchParams(
                firebaseConfig as Record<string, string>
            ).toString()
            const registration = await navigator.serviceWorker.register(
                `/firebase-messaging-sw.js?${swParams}`
            )

            await navigator.serviceWorker.ready;

console.log("SW Active:", registration.active);


            const app = getApps().length ? getApps()[0] : initializeApp(firebaseConfig)
            const messaging = getMessaging(app)

            const token = await getToken(messaging, {
                vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
                serviceWorkerRegistration: await navigator.serviceWorker.ready,
            })

            if (token) {
                registerNovuDeviceToken(token, 'web')
            } else {
                console.log('No web push token was returned')
            }

            // Foreground messages don't trigger the service worker's push event,
            // so we show + handle them manually here.
            // onMessage(messaging, (payload) => {
            //     console.log("🔥 FCM RECEIVED:", payload);
            //     const data = payload?.data as NotificationClickData | undefined
            //     const title = payload?.notification?.title || 'New notification'
            //     const body = payload?.notification?.body || ''

            //     if (Notification.permission === 'granted') {
            //         const notification = new Notification(title, { body, data })
            //         notification.onclick = () => {
            //             window.focus()
            //             if (data) handleNotificationClick(data)
            //             notification.close()
            //         }
            //     }
            // })
        } catch (error) {
            console.error('Novu web push setup error:', error)
        }
    }

    const registerNovuDeviceToken = async (fcmToken: string, platform: 'android' | 'web' | 'ios') => {
        try {
            await callApi({
                apiClientKey: 'help',
                method: 'post',
                url: '/employee/device-token',
                data: { fcmToken, platform, employeeId: Number(employee_id), action: 'register' },
            })
            // Remembered so unregisterNovuDeviceToken can clean this up on logout,
            // without needing the firebase/Capacitor push context to still be mounted.
            localStorage.setItem(constants.NOVU_FCM_TOKEN, fcmToken)
            localStorage.setItem(constants.NOVU_FCM_PLATFORM, platform)
        } catch (error) {
            console.error('Failed to register Novu device token:', error)
        }
    }

    const handleNotificationClick = (data: NotificationClickData) => {
        const { targetScreen, blocker, clickToCall, mobile, isChat, params } = data || {}      
          const parsedParams =
            typeof params === 'string'
                ? JSON.parse(params)
                : params;  
        if (isChat) {
            navigate('/chat', { state: { url: parsedParams?.url } })
        } else if (blocker || targetScreen === 'Support') {
            window.open(parsedParams?.url || '/', '_blank')
        } else if (clickToCall && mobile) {
            util.clickToCall(mobile)
        } else {
            const isHome = ['Home', 'homepage'].includes(
                targetScreen?.toLowerCase() || ''
            );
            navigate(isHome ? '/' : `/employee${parsedParams?.url}` || '/', { replace: true })
        }
    }
}

// Call on logout so this device stops receiving push for the subscriber
// that was just signed out. Must run before the auth session is cleared,
// since it needs a valid request to reach the backend.
export const unregisterNovuDeviceToken = async () => {
    const fcmToken = localStorage.getItem(constants.NOVU_FCM_TOKEN)
    const platform = localStorage.getItem(constants.NOVU_FCM_PLATFORM)
    const employeeId = util.getEmployeeSession()?.employee_id
    console.log("unregisterNovuDeviceToken",!fcmToken);

    if (!fcmToken || !platform || !employeeId) return

    try {
        await callApi({
            apiClientKey: 'help',
            method: 'post',
            url: '/employee/device-token',
            data: { fcmToken, platform, employeeId: Number(employeeId), action: 'unregister' },
        })
    } catch (error) {
        console.error('Failed to unregister Novu device token:', error)
    } finally {
        localStorage.removeItem(constants.NOVU_FCM_TOKEN)
        localStorage.removeItem(constants.NOVU_FCM_PLATFORM)
    }
}

export default useNovuPush